/**
 * FontManager - Frontend font management service for EdgeDocs
 *
 * Provides font loading, caching, and metrics for the EdgeDocs editor.
 * This is the service injected into editors via DocumentOrchestrator.
 */

export interface FontSpec {
  family: string;
  weight: number | string;
  style: 'normal' | 'italic' | 'oblique';
  size: number;
  features?: string[]; // OpenType features
}

export interface LoadedFont {
  fontFace: FontFace;
  family: string;
  weight: string;
  style: string;
  features: Set<string>; // Supported OpenType features
  scripts: Set<string>; // Supported scripts (Latn, Cyrl, etc.)
  status: 'loading' | 'loaded' | 'error';
  loadTime: number;
  fileSize: number;
  version?: string;
}

export interface FontLoadingConfig {
  supportedFormats: string[];
  harfbuzzFeatures: string[];
  preloadFonts: string[];
  cacheStrategy: 'aggressive' | 'conservative' | 'minimal';
  fallbackStrategy: 'immediate' | 'delayed' | 'none';
}

export interface LegalFontStandards {
  courtRequiredFonts: {
    federal: string[];
    california: string[];
    newYork: string[];
    texas: string[];
    florida: string[];
  };
  firmCustomFonts: string[];
  professionalFonts: string[];
  fallbackChains: Map<string, string[]>;
}

export class FontManager {
  private loadedFonts: Map<string, LoadedFont> = new Map();
  private loadingPromises: Map<string, Promise<LoadedFont>> = new Map();
  private config: FontLoadingConfig;
  private legalStandards: LegalFontStandards;
  private fontCache: Map<string, ArrayBuffer> = new Map();

  // Performance tracking
  private loadingMetrics: {
    totalLoads: number;
    successfulLoads: number;
    failedLoads: number;
    averageLoadTime: number;
    cacheHitRate: number;
  } = {
    totalLoads: 0,
    successfulLoads: 0,
    failedLoads: 0,
    averageLoadTime: 0,
    cacheHitRate: 0
  };

  constructor(options: Partial<FontLoadingConfig> = {}) {
    this.config = {
      supportedFormats: ['woff2', 'woff', 'ttf', 'otf'],
      harfbuzzFeatures: [],
      preloadFonts: ['Times New Roman', 'Arial', 'Calibri'],
      cacheStrategy: 'conservative',
      fallbackStrategy: 'immediate',
      ...options
    };

    this.legalStandards = this.initializeLegalStandards();
    this.initializeFontCache();
    this.preloadCriticalFonts();

    console.log('🔤 [FontManager] Frontend font service initialized');
  }

  /**
   * Prepare fonts for use (called by DocumentOrchestrator)
   */
  async prepare(): Promise<void> {
    console.log('🔤 [FontManager] Preparing fonts...');

    // Preload critical fonts
    const preloadPromises = this.config.preloadFonts.map(fontFamily =>
      this.loadFont({
        family: fontFamily,
        weight: 400,
        style: 'normal',
        size: 12
      }).catch(error => {
        console.warn(`Failed to preload font ${fontFamily}:`, error);
      })
    );

    await Promise.allSettled(preloadPromises);
    console.log('🔤 [FontManager] Font preparation complete');
  }

  /**
   * Load a font by specification
   */
  async loadFont(fontSpec: FontSpec): Promise<LoadedFont> {
    const fontKey = this.getFontKey(fontSpec);

    // Check if already loaded
    const cached = this.loadedFonts.get(fontKey);
    if (cached && cached.status === 'loaded') {
      this.loadingMetrics.cacheHitRate =
        (this.loadingMetrics.cacheHitRate * this.loadingMetrics.totalLoads + 1) /
        (this.loadingMetrics.totalLoads + 1);
      return cached;
    }

    // Check if currently loading
    const loading = this.loadingPromises.get(fontKey);
    if (loading) {
      return loading;
    }

    // Start loading
    const loadPromise = this.loadFontLegacy(fontSpec);
    this.loadingPromises.set(fontKey, loadPromise);

    try {
      const loadedFont = await loadPromise;
      this.loadingPromises.delete(fontKey);
      return loadedFont;
    } catch (error) {
      this.loadingPromises.delete(fontKey);
      throw error;
    }
  }

  /**
   * Get font metrics for layout calculations
   */
  getFontMetrics(family: string, size: number): { ascent: number; descent: number; lineHeight: number } {
    // For Phase 1A, return basic metrics
    // In Phase 1B, this would use actual font metrics
    const lineHeight = size * 1.2; // Standard line height
    const ascent = size * 0.8;     // Approximate ascent
    const descent = size * 0.2;    // Approximate descent

    return { ascent, descent, lineHeight };
  }

  /**
   * Check if font is available
   */
  isFontAvailable(family: string): boolean {
    const fontKey = this.getFontKey({ family, weight: 400, style: 'normal', size: 12 });
    const loaded = this.loadedFonts.get(fontKey);
    return loaded?.status === 'loaded';
  }

  /**
   * Get fallback font chain for a font family
   */
  getFallbackChain(primaryFont: string): string[] {
    const chain = this.legalStandards.fallbackChains.get(primaryFont);
    return chain || [primaryFont, 'serif'];
  }

  /**
   * Get performance metrics
   */
  getMetrics(): typeof this.loadingMetrics {
    return { ...this.loadingMetrics };
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private initializeLegalStandards(): LegalFontStandards {
    const standards: LegalFontStandards = {
      courtRequiredFonts: {
        federal: ['Times New Roman', 'Courier New'],
        california: ['Times New Roman', 'Courier New'],
        newYork: ['Times New Roman', 'Courier New'],
        texas: ['Times New Roman', 'Courier New'],
        florida: ['Times New Roman', 'Courier New']
      },
      firmCustomFonts: [],
      professionalFonts: ['Times New Roman', 'Arial', 'Calibri', 'Georgia', 'Verdana'],
      fallbackChains: new Map([
        ['Times New Roman', ['Times', 'Liberation Serif', 'serif']],
        ['Arial', ['Helvetica', 'Liberation Sans', 'sans-serif']],
        ['Calibri', ['Carlito', 'Liberation Sans', 'sans-serif']],
        ['Courier New', ['Courier', 'Liberation Mono', 'monospace']]
      ])
    };

    return standards;
  }

  private initializeFontCache(): void {
    // Initialize any cached font data
    console.log('🔤 [FontManager] Font cache initialized');
  }

  private async preloadCriticalFonts(): Promise<void> {
    // Preload critical fonts in background
    if (this.config.cacheStrategy === 'aggressive') {
      setTimeout(() => {
        this.config.preloadFonts.forEach(fontFamily => {
          this.loadFont({
            family: fontFamily,
            weight: 400,
            style: 'normal',
            size: 12
          }).catch(() => {
            // Ignore preload failures
          });
        });
      }, 100);
    }
  }

  private async loadFontLegacy(fontSpec: FontSpec): Promise<LoadedFont> {
    const startTime = performance.now();

    try {
      // For Phase 1A, we use system fonts, so we create a FontFace for web fonts
      // In Phase 1B, this would load actual font files

      const fontFace = new FontFace(
        `${fontSpec.family}`,
        `local('${fontSpec.family}')`, // Use system font
        {
          weight: typeof fontSpec.weight === 'number' ? fontSpec.weight.toString() : '400',
          style: fontSpec.style
        }
      );

      // Load the font (though it may already be available)
      await fontFace.load();

      // Add to document
      document.fonts.add(fontFace);

      const loadedFont: LoadedFont = {
        fontFace,
        family: fontSpec.family,
        weight: typeof fontSpec.weight === 'number' ? fontSpec.weight.toString() : fontSpec.weight,
        style: fontSpec.style,
        features: new Set(fontSpec.features || []),
        scripts: new Set(['Latn']), // Basic Latin support
        status: 'loaded',
        loadTime: performance.now() - startTime,
        fileSize: 0 // System fonts have no file size
      };

      const fontKey = this.getFontKey(fontSpec);
      this.loadedFonts.set(fontKey, loadedFont);
      this.loadingMetrics.successfulLoads++;
      this.loadingMetrics.totalLoads++;

      return loadedFont;

    } catch (error) {
      this.loadingMetrics.failedLoads++;
      this.loadingMetrics.totalLoads++;
      throw new Error(`Failed to load font ${fontSpec.family}: ${error}`);
    }
  }

  private getFontKey(fontSpec: FontSpec): string {
    return `${fontSpec.family}-${fontSpec.weight}-${fontSpec.style}`;
  }

  /**
   * Get fallback font when loading fails
   */
  private getFallbackFont(fontSpec: FontSpec): LoadedFont {
    // Return a basic fallback font
    const fallbackFont: LoadedFont = {
      fontFace: null as any,
      family: 'serif', // Generic fallback
      weight: '400',
      style: 'normal',
      features: new Set(),
      scripts: new Set(['Latn']),
      status: 'loaded',
      loadTime: 0,
      fileSize: 0
    };

    return fallbackFont;
  }
}




